import { useCallback, useEffect, useState } from "react";
import { listBuddyGuarantees } from "./buddyGuaranteeApi.js";

/**
 * Fetches one real source person's own buddy-guarantee records directly
 * (`workerApplicationId`/`workerProfileId` filter) - the person-detail
 * drawer's own data source (Prompt 140B-R1). Unlike the old anchor-buddy-
 * id detail fetch this replaces, this works even when the person
 * currently has zero buddies (nothing to anchor on) - the whole point of
 * the person-first redesign. `buddyStatus` defaults to "Active" (the
 * default, calm view); the drawer's own secondary audit toggle passes
 * `undefined` to see every status, matching the same "default hides
 * revoked, an explicit secondary control reveals it" rule the main roster
 * already follows.
 */
export function useSourcePersonBuddies(source, { buddyStatus = "Active" } = {}) {
  const [state, setState] = useState({ status: "idle", items: [], totalCount: null, error: null });
  const [reloadToken, setReloadToken] = useState(0);

  const workerApplicationId = source?.workerApplicationId ?? null;
  const workerProfileId = source?.workerProfileId ?? null;

  useEffect(() => {
    if (!workerApplicationId && !workerProfileId) {
      setState({ status: "idle", items: [], totalCount: null, error: null });
      return undefined;
    }

    let cancelled = false;
    setState((previous) => ({ status: "loading", items: previous.items, totalCount: previous.totalCount, error: null }));

    listBuddyGuarantees({
      workerApplicationId: workerApplicationId ?? undefined,
      workerProfileId: workerProfileId ?? undefined,
      buddyStatus,
      pageSize: 10,
    })
      .then((data) => {
        if (!cancelled) {
          setState({ status: "ready", items: data?.items ?? [], totalCount: data?.totalCount ?? 0, error: null });
        }
      })
      .catch((error) => {
        if (!cancelled) {
          setState({ status: "error", items: [], totalCount: null, error });
        }
      });

    return () => {
      cancelled = true;
    };
  }, [workerApplicationId, workerProfileId, buddyStatus, reloadToken]);

  const refetch = useCallback(() => setReloadToken((token) => token + 1), []);

  return { ...state, refetch };
}
